import { useId, useReducer, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'
import { apiErrorCode, createContactNote, fetchContactNotes, updateContactNote, type ContactNoteItem } from '@/api/lcCrm'
import { handleApiError } from '@/api/client'
import { contactNoteEditReducer, isValidContactNoteContent } from './contactNoteEdit'
import { ErrorBlock, LoadingBlock } from './crmComponents'
import { formatKstDateTime } from './crmLabels'

/** Contact ownership preserves the same notes before and after lead conversion. */
export function ContactNotesPanel({ contactId }: { contactId: number }) {
  const queryClient = useQueryClient()
  const [content, setContent] = useState('')
  const queryKey = ['lc-crm-contact-notes', contactId]
  const notes = useQuery({ queryKey, queryFn: () => fetchContactNotes(contactId) })
  const refresh = () => queryClient.invalidateQueries({ queryKey })
  const create = useMutation({
    mutationFn: () => createContactNote(contactId, content.trim()),
    onSuccess: async () => {
      setContent('')
      await refresh()
      toast.success('메모를 저장했습니다')
    },
    onError: (error) => toast.error(handleApiError(error) || '메모 저장 실패'),
  })
  const updated = async (note: ContactNoteItem) => {
    queryClient.setQueryData<ContactNoteItem[]>(queryKey, (current) =>
      current?.map((item) => item.noteId === note.noteId ? note : item),
    )
    await refresh()
  }

  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label="접수함 메모">
      <div className="min-w-0">
        <h3 className="font-semibold">접수함 메모</h3>
        <p className="text-sm text-muted-foreground">연락처별로 누적되며 리드 전환 후에도 이어서 볼 수 있습니다.</p>
      </div>
      <form className="flex min-w-0 flex-col items-end gap-2" onSubmit={(event) => {
        event.preventDefault()
        if (isValidContactNoteContent(content) && !create.isPending) create.mutate()
      }}>
        <FieldGroup className="min-w-0">
          <Field>
            <FieldLabel htmlFor={`contact-note-${contactId}`}>새 메모</FieldLabel>
            <Textarea id={`contact-note-${contactId}`} rows={3} value={content} maxLength={10000}
              placeholder="참석 확인, 재안내, 일정 변경 등 연락 내용을 남겨 주세요."
              onChange={(event) => setContent(event.target.value)} disabled={create.isPending} />
          </Field>
        </FieldGroup>
        <Button type="submit" disabled={!isValidContactNoteContent(content) || create.isPending}>
          {create.isPending && <Spinner data-icon="inline-start" />}
          메모 저장
        </Button>
      </form>
      {notes.isLoading && <LoadingBlock />}
      {notes.error && <ErrorBlock message="메모를 불러오지 못했습니다." />}
      {notes.data?.length === 0 && <p className="text-sm text-muted-foreground">작성된 메모가 없습니다.</p>}
      {notes.data && notes.data.length > 0 && (
        <ol className="flex min-w-0 flex-col gap-3">
          {notes.data.map((note) => (
            <ContactNoteRow key={note.noteId} note={note} isRefreshing={notes.isFetching}
              onRefresh={refresh} onUpdated={updated} />
          ))}
        </ol>
      )}
    </section>
  )
}

function ContactNoteRow({ note, isRefreshing, onRefresh, onUpdated }: {
  note: ContactNoteItem
  isRefreshing: boolean
  onRefresh: () => Promise<void>
  onUpdated: (note: ContactNoteItem) => Promise<void>
}) {
  const editorId = useId()
  const [draft, dispatch] = useReducer(contactNoteEditReducer, null)
  const newerVersionAvailable = draft != null && note.version !== draft.expectedVersion
  const conflict = draft != null && (draft.stale || newerVersionAvailable)
  const save = useMutation({
    mutationFn: (payload: { content: string; expectedVersion: number }) =>
      updateContactNote(note.contactId, note.noteId, payload),
    onSuccess: async (saved) => {
      dispatch({ type: 'finish' })
      await onUpdated(saved)
      toast.success('메모를 수정했습니다')
    },
    onError: async (error) => {
      if (apiErrorCode(error) === 'CRM_NOTE_STALE') {
        dispatch({ type: 'stale' })
        toast.error('다른 운영자가 메모를 수정했습니다. 입력 내용은 유지했습니다. 최신 내용을 확인해 주세요.')
        await onRefresh()
      } else toast.error(handleApiError(error) || '메모 수정 실패')
    },
  })

  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-md border border-border p-3" data-note-id={note.noteId}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>작성 <time dateTime={note.createdAt} data-slot="contact-note-created-at">{formatKstDateTime(note.createdAt)}</time></span>
          {note.updatedAt && <span>수정 <time dateTime={note.updatedAt} data-slot="contact-note-updated-at">{formatKstDateTime(note.updatedAt)}</time></span>}
        </div>
        {!draft && <Button type="button" variant="outline" size="sm" onClick={() => dispatch({ type: 'start', note })}>수정</Button>}
      </div>
      {draft ? (
        <form className="flex min-w-0 flex-col gap-3" onSubmit={(event) => {
          event.preventDefault()
          if (isValidContactNoteContent(draft.content) && !save.isPending && !conflict) {
            save.mutate({ content: draft.content.trim(), expectedVersion: draft.expectedVersion })
          }
        }}>
          <FieldGroup className="min-w-0">
            <Field>
              <FieldLabel htmlFor={editorId}>메모 내용</FieldLabel>
              <Textarea id={editorId} rows={4} value={draft.content} maxLength={10000}
                disabled={save.isPending} onChange={(event) => dispatch({ type: 'change', content: event.target.value })} />
            </Field>
          </FieldGroup>
          {conflict && !save.isPending && <Alert className="min-w-0 [overflow-wrap:anywhere]">
            <AlertTitle>다른 운영자가 메모를 수정했습니다</AlertTitle>
            <AlertDescription className="min-w-0">
              <p>입력한 내용은 유지했습니다. 최신 저장 내용을 확인한 뒤 계속 수정해 주세요.</p>
              {newerVersionAvailable ? <div className="flex min-w-0 flex-col gap-2">
                <span className="font-medium">최신 저장 내용</span>
                <p className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]" data-slot="contact-note-latest-content">{note.content}</p>
                <span>{note.updatedAt ? '수정' : '작성'} {formatKstDateTime(note.updatedAt ?? note.createdAt)}</span>
                <Button type="button" variant="outline" className="max-w-full self-start" disabled={save.isPending || isRefreshing}
                  onClick={() => dispatch({ type: 'review', latestVersion: note.version })}>최신 내용 확인 후 계속 수정</Button>
              </div> : <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span>최신 내용을 불러오는 중입니다.</span>
                <Button type="button" variant="outline" disabled={save.isPending || isRefreshing} onClick={() => { void onRefresh() }}>최신 내용 다시 불러오기</Button>
              </div>}
            </AlertDescription>
          </Alert>}
          <div className="flex min-w-0 flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={save.isPending} onClick={() => dispatch({ type: 'finish' })}>취소</Button>
            <Button type="submit" disabled={!isValidContactNoteContent(draft.content) || save.isPending || conflict}>
              {save.isPending && <Spinner data-icon="inline-start" />}
              수정 저장
            </Button>
          </div>
        </form>
      ) : <p className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]" data-slot="contact-note-content">{note.content}</p>}
    </li>
  )
}
